<template>
  <b-modal
    id="add-or-update-country"
    :visible="isAddOrUpdateActive"
    centered
    hide-footer
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    @hidden="resetForm"
    @hide="resetForm"
    @change="(val) => $emit('update:is-add-or-update-active', val)"
  >
    <template #default="{ hide }">
      <b-overlay :show="loading" class="px-2">
        <div class="d-flex justify-content-between align-items-center content-sidebar-header py-1" style="border-bottom: 1px solid #000000;">
          <h5 class="mb-0" style="font-weight: 400; font-size: 14px; line-height: 160%;">
            Update Country
          </h5>

          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="hide"
          />
        </div>

        <validation-observer
          #default="{ handleSubmit }"
          ref="update_modal_form"
        >
          <b-form
            class="my-1"
            @submit.prevent="handleSubmit(onUpdate)"
            @reset.prevent="resetForm"
          >

            <b-alert
              v-if="errorMessage"
              variant="danger"
              :show="true"
            >
              <div class="alert-body font-small-2">
                <b-card-text class="mb-0">
                  {{ errorMessage }}
                </b-card-text>
              </div>
            </b-alert>

            <b-form-group label="Country" label-for="country">
              <b-form-input id="country" :value="new_data.country" disabled />
            </b-form-group>

            <!-- Supported -->
            <b-form-group label="Active Mission Country?" label-for="supported" class="mb-2">
              <validation-provider
                #default="{ errors }"
                name="supported"
                rules=""
              >
                <b-form-checkbox
                  id="supported"
                  v-model="new_data.supported"
                  :disabled="disableForm"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- RMC -->
            <b-form-group label="RMC" label-for="rmc">
              <validation-provider
                #default="{ errors }"
                name="RMC"
                rules=""
              >
                <v-select
                  id="rmc"
                  v-model="new_data.rmc_id"
                  :loading="rmcLoading"
                  :disabled="disableForm"
                  :options="rmcOptions"
                  :reduce="(val) => val.value"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Mission Bloc -->
            <b-form-group label="Mission Bloc" label-for="block" class="mb-2">
              <template #label>
                <span>Mission Bloc</span>
                <small v-if="new_data.rmc_id" class="text-muted"> (derived from RMC)</small>
              </template>

              <v-select
                id="block"
                v-model="new_data.block_id"
                :loading="blocksLoading"
                :disabled="disableForm || !!new_data.rmc_id"
                :options="blockOptions"
                :reduce="(val) => val.value"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              />
            </b-form-group>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-2"
                type="submit"
                :disabled="disableForm"
              >
                Update
              </b-button>

              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-secondary"
                @click="hide"
              >
                Cancel
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import {
  BOverlay, BForm, BFormGroup, BFormInput, BFormCheckbox,
  BFormInvalidFeedback, BButton, BAlert, BCardText, BModal,
} from 'bootstrap-vue'

import { get, cloneDeep, omit } from 'lodash';
import { ValidationProvider, ValidationObserver } from 'vee-validate'

import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const rawCountryData = {
  country: "",
  supported: false,
  rmc_id: null,
  block_id: null,
}

export default {
  components: {
    BForm,
    BAlert,
    BModal,
    BButton,
    vSelect,
    BOverlay,
    BCardText,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BFormInvalidFeedback,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isAddOrUpdateActive',
    event: 'update:is-add-or-update-active',
  },
  props: {
    isAddOrUpdateActive: {
      type: Boolean,
      required: true,
    },
    updateData: {
      type: Object,
      default: null,
      required: false
    },
  },
  data() {
    return {
      new_data: cloneDeep(rawCountryData),
      loading: false,
      rmcLoading: false,
      blocksLoading: false,
      errorMessage: "",
      rmcOptions: [],
      blockOptions: [],
    }
  },
  computed: {
    disableForm(){
      return !this.can("countries:CanUpdate")
    },
  },
  watch: {
    'new_data.rmc_id': function onRmcIdChange(rmc_id) {
      if (rmc_id) {
        const rmc = this.rmcOptions.find((option) => option.value === rmc_id);
        if (rmc && rmc.block_id) {
          this.new_data.block_id = rmc.block_id;
        }
      }
    },
    updateData: {
      handler(updateData){
        if (updateData){
          this.new_data = {
            _id: updateData._id,
            country: updateData.country,
            supported: !!updateData.supported,
            rmc_id: updateData.rmc_id || null,
            block_id: updateData.block_id || null,
          }
        }
      },
      immediate: true,
      deep: true
    },
  },
  mounted() {
    this.fetchRmcOptions()
    this.fetchMissionBlocks()
  },
  methods: {
    async onUpdate(){
      try {
        this.loading = true;
        this.errorMessage = "";

        const success = await this.$refs.update_modal_form.validate();
        if (!success){
          return;
        }

        const result = await this.$swal({
          title: "Confirm Country Update",
          text: "You are about to update details of a country.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });
        if (!result.isConfirmed) {
          return;
        }

        const id = this.new_data._id
        const update_data = omit(this.new_data, ["_id", "country"]);
        await this.useJwt().sharedService.updateGeoCountry(id, update_data);

        this.$emit("refetchData");
        this.$emit('update:is-add-or-update-active', false);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'Country updated successfully'
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async fetchRmcOptions() {
      try {
        this.rmcLoading = true;

        const request = await this.useJwt().sharedService.fetchRmc({ limit: 500 });
        const { data } = request.data;

        this.rmcOptions = (data || []).map((item) => ({
          label: item.title,
          value: item._id,
          block_id: item.block_id,
        }))
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.rmcLoading = false;
      }
    },
    async fetchMissionBlocks() {
      try {
        this.blocksLoading = true;

        const request = await this.useJwt().sharedService.fetchMissionBlocks({ limit: 500 });
        const { data } = request.data;

        this.blockOptions = (data || []).map((item) => ({
          label: item.title,
          value: item._id
        }))
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.blocksLoading = false;
      }
    },

    resetForm() {
      this.$emit("resetUpdateData")
      this.new_data = cloneDeep(rawCountryData);
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

#add-or-update-country {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
